// React
import React from "react";

import { Box } from "@mui/material";
import Popper from "@mui/material/Popper";
// MUI
import { createTheme, styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";

// Styles
import { colors, radii, shadows as shadowTokens } from "styles/tokens";

// Form fields (TextField, Select, Autocomplete, date pickers, native inputs). What a user has to
// find, a field's outline, is at least 3:1 against white and #F2F2F2 (WCAG 1.4.11), and its text
// at least 4.5:1. styles/global.css gives the same outline to native inputs with border-gray-300.
export const fieldColors = {
  border: colors.fieldBorder, // #7B818C: 3.9:1 on white, 3.5:1 on #F2F2F2
  borderHover: "#424242",
  label: "#616161", // 6.2:1
  labelFocused: "#1958B3", // the primary darkened: #247DFF is 3.9:1, too light for small text
  error: "#D32F2F", // 5:1: #FF0000 is 4:1, too light for helper text
  disabledBackground: "#F5F5F5",
  disabledBorder: "#D4D7DC",
  disabledText: "#6B6B6B", // 5.3:1: a disabled value stays readable
};

// Focus ring of every field (MUI and native .field): primary at 18%
export const FOCUS_RING = "0 0 0 3px rgba(36, 125, 255, 0.18)";

// Outline of MUI's outlined fields and of the date pickers' own (same class names, own prefix).
// The hover colour skips the states that have their own, and stays low in specificity so a
// component's sx still wins.
const outlinedFieldOverrides = (prefix) => ({
  root: {
    // One field style for the whole app (same as .field in styles/global.css): white, 8px radius,
    // 14px text, primary outline plus a soft ring on focus
    borderRadius: radii.control,
    backgroundColor: colors.white,
    fontSize: 14,
    [`&.Mui-focused .${prefix}-notchedOutline`]: {
      borderColor: colors.primary,
      borderWidth: 1,
    },
    "&.Mui-focused": {
      boxShadow: FOCUS_RING,
    },
    // Icons inside a field (password eye, clear, calendar) must not make it taller than 36px
    "& .MuiIconButton-root": {
      padding: 4,
    },
    // Touch screens: 16px text, or iOS zooms the page on focus
    "@media (pointer: coarse)": {
      fontSize: 16,
    },
    [`&.Mui-focused.Mui-error .${prefix}-notchedOutline`]: {
      borderColor: fieldColors.error,
    },
    [`&:hover:where(:not(.Mui-focused, .Mui-error, .Mui-disabled)) .${prefix}-notchedOutline`]: {
      borderColor: fieldColors.borderHover,
    },
    "@media (hover: none)": {
      [`&:hover:where(:not(.Mui-focused, .Mui-error, .Mui-disabled)) .${prefix}-notchedOutline`]: {
        borderColor: fieldColors.border,
      },
    },
    [`&.Mui-error .${prefix}-notchedOutline`]: {
      borderColor: fieldColors.error,
    },
    "&.Mui-disabled": {
      backgroundColor: fieldColors.disabledBackground,
      color: fieldColors.disabledText,
    },
    [`&.Mui-disabled .${prefix}-notchedOutline`]: {
      borderColor: fieldColors.disabledBorder,
    },
  },
  // Small fields are 36px like the native .field: 8px + 20px line + 8px. Text 13px from the outer
  // edge, as .field (1px border + 12px): MUI's outline is an absolute fieldset that takes no
  // room, hence 13px of padding. MUI applies the slots below after
  // inputSizeSmall, so the adorned and multiline paddings win over it.
  inputSizeSmall: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 13,
    paddingRight: 13,
    lineHeight: "20px",
    height: "20px",
    "@media (pointer: coarse)": {
      paddingTop: 12,
      paddingBottom: 12,
    },
  },
  // with an adornment the root carries the side padding (adornedStart / adornedEnd)
  inputAdornedStart: {
    paddingLeft: 0,
  },
  inputAdornedEnd: {
    paddingRight: 0,
  },
  adornedStart: {
    paddingLeft: 12,
  },
  adornedEnd: {
    paddingRight: 8,
  },
  // multiline: the root has the padding, the textarea none (no double padding)
  multiline: {
    padding: "8px 13px",
  },
  inputMultiline: {
    padding: 0,
  },
  notchedOutline: {
    borderColor: fieldColors.border,
    // the gap for the label follows the label's size (0.75rem), not the field's: a field set to
    // 14px cut a shorter gap and the outline ran through the label
    "& legend": {
      fontSize: "0.75rem",
    },
  },
});

// MUI's placeholder is the text colour at 42%, 3:1 on white: 60% is 5.7:1.
const PLACEHOLDER_OPACITY = 0.6;
const placeholder = { opacity: PLACEHOLDER_OPACITY };

// Elevation 1-2 (Card, Accordion, Paper by default): the soft shadow of the notes' cards with a
// hairline ring instead of MUI's grey drop shadow. Menus and popovers get theirs below.
const baseShadows = createTheme().shadows;
const appShadows = [...baseShadows];
appShadows[1] = `0 0 0 1px rgba(16, 24, 40, 0.06), ${shadowTokens.card}`;
appShadows[2] = `0 0 0 1px rgba(16, 24, 40, 0.06), 0 2px 6px rgba(16, 24, 40, 0.06)`;

// Floating surfaces: menus, popovers (also MUI Select's list)
const floatingPaper = {
  borderRadius: radii.card,
  border: `1px solid ${colors.gray200}`,
  boxShadow: shadowTokens.popover,
};

// global theme
// Accent and neutrals come from styles/tokens.js (same values as the @theme of global.css).
// The type scale is set in MuiTypography.styleOverrides on purpose: theme.typography.body1/2
// also feed fields, menu items and grid cells, which keep MUI's sizes.
// Button scale (same numbers as .btn in styles/global.css): height, padding of contained/text
// (outlined is 1px less on each side, for its border), font size, icon size and icon gap.
const BUTTON_SIZES = {
  small: { height: 32, py: 6, px: 12, fontSize: 13, icon: 16, gap: 6 },
  medium: { height: 36, py: 8, px: 16, fontSize: 14, icon: 18, gap: 8 },
  large: { height: 40, py: 10, px: 20, fontSize: 15, icon: 20, gap: 8 },
};

const buttonRoot = ({ ownerState }) => {
  const size = BUTTON_SIZES[ownerState.size] || BUTTON_SIZES.medium;
  const variant = ownerState.variant || "text";
  const border = variant === "outlined" ? 1 : 0;
  const iconSx = { fontSize: size.icon };
  return {
    textTransform: "none",
    fontWeight: 500,
    borderRadius: radii.control,
    boxShadow: "none",
    minHeight: size.height,
    padding: `${size.py - border}px ${size.px - border}px`,
    fontSize: size.fontSize,
    "& .MuiButton-startIcon": { marginLeft: -2, marginRight: size.gap },
    "& .MuiButton-endIcon": { marginRight: -2, marginLeft: size.gap },
    "& .MuiButton-startIcon > svg, & .MuiButton-endIcon > svg": iconSx,
    ...(variant === "text" && ownerState.size === "small" && { minWidth: 0 }),
    // neutral secondary: <Button variant="outlined" color="inherit">
    ...(ownerState.color === "inherit" &&
      variant === "outlined" && {
        borderColor: colors.gray300,
        color: colors.gray700,
        "&:hover": { backgroundColor: colors.gray50, borderColor: colors.gray400 },
      }),
    // ghost: <Button variant="text" color="inherit">
    ...(ownerState.color === "inherit" &&
      variant === "text" && {
        color: colors.gray700,
        "&:hover": { backgroundColor: colors.gray100 },
      }),
  };
};

// Icon button: 8px radius at every size (square with round corners, like ActionMenuButton).
// small 28 (4 + 20), medium 36 (6 + 24), large 40 (8 + 24). Fields keep their own padding 4.
const ICON_BUTTON_PADDING = { small: 4, medium: 6, large: 8 };
const iconButtonRoot = ({ ownerState }) => ({
  borderRadius: radii.control,
  padding: ICON_BUTTON_PADDING[ownerState.size] ?? ICON_BUTTON_PADDING.medium,
  ...(ownerState.size === "small" && {
    // icons left at their default size; fontSize="small"/"inherit" or an icon class keep theirs
    "& .MuiSvgIcon-fontSizeMedium": { fontSize: 20 },
  }),
  "@media (pointer: coarse)": {
    minWidth: 40,
    minHeight: 40,
  },
});

export const theme = createTheme({
  typography: {
    fontFamily: "Inter, Arial, sans-serif",
    button: { lineHeight: "20px", fontWeight: 500, textTransform: "none" },
  },
  shadows: appShadows,
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryStrong,
      main_r: "36",
      main_g: "125",
      main_b: "255",
    },
    error: {
      main: colors.danger,
    },
    text: {
      primary: colors.gray900,
      // MUI's labels, helper texts, empty checkboxes and radios: white made them vanish
      secondary: colors.gray600,
      error: colors.danger,
    },
    divider: colors.gray200,
    background: { default: "#fff" }, // modify this for the background color
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h3: {
          fontSize: "32px",
          lineHeight: 1.25,
          fontWeight: "600",
          fontFamily: "Inter",
          color: colors.gray900,
          textAlign: "left",
        },
        h4: {
          fontSize: "22px",
          lineHeight: 1.3,
          fontWeight: "600",
          fontFamily: "Inter",
          color: colors.gray900,
          textAlign: "left",
        },
        h6: {
          fontSize: "18px",
          lineHeight: 1.4,
          fontWeight: "600",
          fontFamily: "Inter",
          color: colors.gray900,
          textAlign: "left",
        },
        overline: {
          color: colors.gray900,
          fontFamily: "Inter, Arial, sans-serif",
          fontSize: "15px",
          fontStyle: "normal",
          fontWeight: "500",
          wordWrap: "break-word",
          textTransform: "capitalize",
        },
        body1: {
          color: colors.gray900,
          fontFamily: "Inter, Arial, sans-serif",
          fontSize: "15px",
          lineHeight: 1.5,
          fontStyle: "normal",
          fontWeight: "400",
          wordWrap: "break-word",
        },
        body2: {
          color: colors.gray900,
          fontSize: "14px",
          lineHeight: 1.45,
          fontWeight: "400",
        },
        subtitle1: {
          fontFamily: "Inter, Arial, sans-serif",
          fontSize: "13px",
          fontWeight: "400",
        },
        subtitle2: {
          fontFamily: "Inter, Arial, sans-serif",
          fontSize: "12px",
          fontWeight: "400",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: buttonRoot,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: iconButtonRoot,
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 4,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: floatingPaper,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: "20px",
          minHeight: 34,
          padding: "6px 10px",
          borderRadius: radii.control,
          color: colors.gray900,
          "@media (min-width: 600px)": {
            minHeight: 34,
          },
          "@media (hover: none)": {
            minHeight: 40,
          },
          "& .MuiListItemIcon-root": {
            minWidth: 30,
            color: colors.gray500,
          },
          "&:hover": {
            backgroundColor: colors.gray100,
          },
          "&.Mui-focusVisible": {
            backgroundColor: colors.gray100,
          },
          "&.Mui-selected, &.Mui-selected.Mui-focusVisible": {
            backgroundColor: colors.primarySoft,
          },
          "&.Mui-selected:hover": {
            backgroundColor: colors.primarySoft,
          },
        },
        dense: {
          minHeight: 32,
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.gray900,
          color: "#fff",
          fontSize: 12,
          fontWeight: 500,
          lineHeight: "16px",
          borderRadius: 6,
          padding: "6px 8px",
        },
        arrow: {
          color: colors.gray900,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.dialog,
        },
        paperFullScreen: {
          borderRadius: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
          gap: 8,
          // spacing comes from the gap
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 18,
          fontWeight: 600,
          color: colors.gray900,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radii.pill,
          fontWeight: 500,
        },
        // small: status, secondary filters; medium: StatusChip. Card chips: components/ui/CardChip
        sizeSmall: { height: 24, fontSize: 12 },
        labelSmall: { padding: "0 8px" },
        sizeMedium: { height: 32, fontSize: 13 },
        labelMedium: { padding: "0 10px" },
      },
    },
    MuiCheckbox: {
      defaultProps: { size: "small" },
    },
    MuiRadio: {
      defaultProps: { size: "small" },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.card,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        row: {
          border: "none !important",
          color: "#424242", // legacy neutral text of the grids (no token)
        },
        cell: {
          border: "none !important",
          "& .MuiInputBase-root": {
            padding: "5px",
            "& fieldset": {
              borderColor: colors.white,
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary,
            },
          },
          "& .MuiInputBase-input": {
            background: colors.white,
            padding: "5px",
            "& fieldset": {
              borderColor: colors.white,
            },
          },
          "&:focus-within": {
            // This selector targets the cell itself when it's focused
            outline: "none !important",
          },
          "& .MuiDataGrid-cell:focus": {
            // This selector targets the inner cell element directly
            outline: "none !important",
          },
        },
        columnHeader: {
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 13,
            fontWeight: 600,
            color: colors.gray600,
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: "#00000077",
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          background: "#fff",
          "&::before": {
            borderBottomColor: fieldColors.border,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": placeholder,
          "&::-webkit-input-placeholder": placeholder,
          "&::-moz-placeholder": placeholder,
          "label[data-shrink=false] + .MuiInputBase-formControl &:focus::-webkit-input-placeholder":
            placeholder,
          "label[data-shrink=false] + .MuiInputBase-formControl &:focus::-moz-placeholder":
            placeholder,
          "&.Mui-disabled": {
            WebkitTextFillColor: fieldColors.disabledText,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: outlinedFieldOverrides("MuiOutlinedInput"),
    },
    MuiPickersOutlinedInput: {
      styleOverrides: (() => {
        const base = outlinedFieldOverrides("MuiPickersOutlinedInput");
        return {
          ...base,
          // pickers have no input element: the side padding is on the root (MUI: 0 14px)
          root: { ...base.root, paddingLeft: 13, paddingRight: 13 },
          // 8 + 20 + 8 = 36px, like the other fields (MUI small: 8.5px and a taller line)
          sectionsContainer: {
            padding: "8px 0",
            lineHeight: "20px",
            // sections and their content have line-height 1.4375em (20.125px): 36.1px without this
            "& .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent": {
              lineHeight: "20px",
            },
          },
        };
      })(),
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: fieldColors.label,
          "&.Mui-focused": {
            color: fieldColors.labelFocused,
          },
          "&.Mui-error": {
            color: fieldColors.error,
          },
          "&.Mui-disabled": {
            color: fieldColors.disabledText,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: fieldColors.label,
          "&.Mui-error": {
            color: fieldColors.error,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // off: black at 38% is 2.7:1 on white
        track: {
          opacity: 0.5,
        },
      },
    },
    // Every field is "small" (36px) and outlined unless a component asks otherwise
    MuiTextField: {
      defaultProps: { size: "small", variant: "outlined" },
    },
    MuiFormControl: {
      defaultProps: { size: "small" },
    },
    MuiAutocomplete: {
      defaultProps: { size: "small" },
      styleOverrides: {
        paper: floatingPaper,
        option: { fontSize: 14 },
        // 4 + 4 + 20 + 4 + 4 = 36px without chips; text 13px from the outer edge (10 + 3, the
        // outline is an absolute fieldset).
        // MUI's own rule is `.root .MuiOutlinedInput-root.MuiInputBase-sizeSmall`: same
        // specificity here, and no paddingRight, which MUI sets for the clear/popup icons.
        inputRoot: {
          "&.MuiInputBase-sizeSmall": {
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 10,
          },
          "&.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
            padding: "4px 4px 4px 3px",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
      styleOverrides: {
        // MUI's min-height 1.4375em (20.125px) made the select 36.1px
        select: { minHeight: 20 },
        icon: { right: 8, color: colors.gray500, fontSize: 20 },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 14 },
        shrink: { fontSize: 14 },
      },
    },
    MuiDatePicker: {
      defaultProps: { slotProps: { textField: { size: "small" } } },
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
        },
      },
    },
    MuiDateTimePicker: {
      defaultProps: { slotProps: { textField: { size: "small" } } },
    },
    MuiTimePicker: {
      defaultProps: { slotProps: { textField: { size: "small" } } },
    },
  },
});
// ios switch
export const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 38,
  height: 22,
  padding: 0,
  marginRight: 10,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#247DFF",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
  },
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    // off: iOS's system grey, 3.3:1 on white (#E9E9EA was 1.2:1)
    backgroundColor: theme.palette.mode === "light" ? "#8E8E93" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));
export const PopperStyledComponent = styled(Popper)(({ theme }) => ({
  border: `1px solid ${
    theme.palette.mode === "light" ? "rgba(149, 157, 165, 0.2)" : "rgb(1, 4, 9)"
  }`,
}));
export const TitleContainer = styled(Box)({
  padding: "0px 30px",
  display: "flex",
  alignItems: "center",
});
